import React, { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { triggerHaptic } from '../../hooks/useMobile'
import './MobileBottomSheet.css'

interface MobileBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  title?: string
  /** Height preset: 'auto' fits content, 'half' is 50vh, 'full' is ~95vh */
  height?: 'auto' | 'half' | 'full'
  /** Show close button in header */
  showCloseButton?: boolean
  /** Show drag handle */
  showDragHandle?: boolean
  /** Callback when sheet is fully closed after animation */
  onClosed?: () => void
  /** Custom header content */
  headerContent?: React.ReactNode
  /** Disable backdrop click to close */
  disableBackdropClose?: boolean
  /** Additional class name */
  className?: string
}

export default function MobileBottomSheet({
  isOpen,
  onClose,
  children,
  title,
  height = 'auto',
  showCloseButton = true,
  showDragHandle = true,
  onClosed,
  headerContent,
  disableBackdropClose = false,
  className = ''
}: MobileBottomSheetProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const sheetRef = useRef<HTMLDivElement>(null)
  const dragStartY = useRef<number | null>(null)
  const currentTranslateY = useRef(0)
  const velocityRef = useRef(0)
  const lastY = useRef(0)
  const lastTime = useRef(Date.now())

  // Handle open/close animations
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true)
      // Small delay to trigger CSS transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true)
        })
      })
      // Lock body scroll
      document.body.style.overflow = 'hidden'
    } else if (isVisible) {
      setIsAnimating(false)
      // Wait for animation to complete
      const timer = setTimeout(() => {
        setIsVisible(false)
        document.body.style.overflow = ''
        onClosed?.()
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [isOpen, isVisible, onClosed])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Drag to dismiss handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY
    lastY.current = e.touches[0].clientY
    lastTime.current = Date.now()
    currentTranslateY.current = 0
    velocityRef.current = 0

    if (sheetRef.current) {
      sheetRef.current.style.transition = 'none'
    }
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (dragStartY.current === null) return

    const currentY = e.touches[0].clientY
    const deltaY = currentY - dragStartY.current
    const now = Date.now()
    const dt = now - lastTime.current

    // Calculate velocity
    if (dt > 0) {
      velocityRef.current = (currentY - lastY.current) / dt
    }
    lastY.current = currentY
    lastTime.current = now

    // Only allow dragging down (positive deltaY)
    if (deltaY > 0) {
      currentTranslateY.current = deltaY
      if (sheetRef.current) {
        sheetRef.current.style.transform = `translateY(${deltaY}px)`
      }
    }
  }, [])

  const handleTouchEnd = useCallback(() => {
    if (dragStartY.current === null) return

    if (sheetRef.current) {
      sheetRef.current.style.transition = ''
      sheetRef.current.style.transform = ''
    }

    // Close if dragged more than 100px or velocity is high
    const shouldClose = currentTranslateY.current > 100 || velocityRef.current > 0.5

    if (shouldClose) {
      triggerHaptic('light')
      onClose()
    }

    dragStartY.current = null
    currentTranslateY.current = 0
    velocityRef.current = 0
  }, [onClose])

  const handleBackdropClick = useCallback(() => {
    if (!disableBackdropClose) {
      triggerHaptic('light')
      onClose()
    }
  }, [disableBackdropClose, onClose])

  if (!isVisible) return null

  const heightClass = {
    auto: 'mobile-sheet-auto',
    half: 'mobile-sheet-half',
    full: 'mobile-sheet-full'
  }[height]

  return createPortal(
    <div className={`mobile-bottom-sheet-container ${isAnimating ? 'open' : ''}`}>
      {/* Backdrop */}
      <div
        className="mobile-sheet-backdrop"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={`mobile-bottom-sheet ${heightClass} ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'sheet-title' : undefined}
      >
        {/* Drag handle */}
        {showDragHandle && (
          <div
            className="mobile-sheet-drag-area"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="mobile-sheet-handle" />
          </div>
        )}

        {/* Header */}
        {(title || showCloseButton || headerContent) && (
          <div className="mobile-sheet-header">
            {title && (
              <h2 id="sheet-title" className="mobile-sheet-title">
                {title}
              </h2>
            )}
            {headerContent}
            {showCloseButton && (
              <button
                className="mobile-sheet-close"
                onClick={() => {
                  triggerHaptic('light')
                  onClose()
                }}
                aria-label="Close"
              >
                <XMarkIcon />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="mobile-sheet-content">
          {children}
        </div>
      </div>
    </div>,
    document.body
  )
}
